// ============================================================================
// STORE.PROVISIONING.JS — Join Request CRUD + Approval Batch
//
// Handles the full provisioning loop:
//   1. Pending user submits a join request → createJoinRequest()
//   2. Pending user polls status           → getJoinRequest()
//   3. Tenant admin lists pending requests  → getJoinRequests()
//   4. Tenant admin approves (atomic batch) → approveJoinRequest()
//   5. Tenant admin denies                  → denyJoinRequest()
//
// All Firestore paths use PATHS builders — no inline path strings.
// Uses getApp() singleton pattern (same as store.referrals.js).
// ============================================================================

import { getApp } from 'firebase/app';
import {
  getFirestore,
  doc, getDoc, getDocs,
  setDoc, updateDoc, writeBatch,
  collection, query, where,
  serverTimestamp,
} from 'firebase/firestore';
import { PATHS } from './firestorePaths.js';

// ---------------------------------------------------------------------------
// DB helper — lazy singleton, avoids double-initialisation
// ---------------------------------------------------------------------------

function getDb() {
  return getFirestore(getApp());
}

// ============================================================================
// createJoinRequest
//
// Called by a pending user on /pending screen.
// Writes: /tenants/{tenantId}/join_requests/{uid}
// Firestore rules enforce: self(uid), user.role == "pending", status == "pending"
// ============================================================================

/**
 * @param {string} tenantId        — The target clinic ID (entered by user)
 * @param {{ uid, email, displayName }} authUser — Firebase auth user object
 * @returns {Promise<void>}
 */
export async function createJoinRequest(tenantId, authUser) {
  if (!tenantId || !tenantId.trim()) throw new Error('tenantId is required');
  if (!authUser?.uid) throw new Error('authUser.uid is required');

  const db = getDb();
  const ref = doc(db, PATHS.joinRequest(tenantId.trim(), authUser.uid));

  await setDoc(ref, {
    uid: authUser.uid,
    tenantId: tenantId.trim(),
    requesterEmail: authUser.email ?? null,
    requesterDisplayName: authUser.displayName ?? null,
    status: 'pending',
    requestedAt: serverTimestamp(),
    decidedAt: null,
    decidedByUid: null,
    provisionedRole: null,
    provisionedSpecialtyIds: [],
  });
}

// ============================================================================
// getJoinRequest
//
// Called by a pending user to check the status of their own request.
// Returns the request data, or null if no request found.
// Firestore rules: allow get if self(requestUid)
// ============================================================================

/**
 * @param {string} tenantId
 * @param {string} uid
 * @returns {Promise<object|null>}
 */
export async function getJoinRequest(tenantId, uid) {
  const db = getDb();
  const ref = doc(db, PATHS.joinRequest(tenantId, uid));
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ============================================================================
// getJoinRequests
//
// Called by tenant_admin to list all requests with a given status.
// Firestore rules: allow list if isTenantAdmin(tenantId)
// ============================================================================

/**
 * @param {string} tenantId
 * @param {'pending'|'approved'|'denied'} [statusFilter='pending']
 * @returns {Promise<object[]>}
 */
export async function getJoinRequests(tenantId, statusFilter = 'pending') {
  const db = getDb();
  const col = collection(db, PATHS.col.joinRequests(tenantId));
  const q = query(col, where('status', '==', statusFilter));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ============================================================================
// approveJoinRequest
//
// Atomic batch: update join_request → update /users/{uid} → setDoc /members/{uid}
// Firestore rules for /users update: provisioning branch (requires join_request exists)
// Firestore rules for /members: isTenantAdmin(tenantId)
// ============================================================================

/**
 * @param {string}   tenantId
 * @param {string}   requestUid              — uid of the user being provisioned
 * @param {string}   role                    — assigned role (not platform_admin)
 * @param {string[]} specialtyIds            — required if role === 'specialist' or 'specialist_admin', else []
 * @param {string}   adminUid               — uid of the approving admin
 * @param {string}   requesterEmail
 * @param {string}   requesterDisplayName
 * @returns {Promise<{ success: true }>}
 */
export async function approveJoinRequest(
  tenantId,
  requestUid,
  role,
  specialtyIds,
  adminUid,
  requesterEmail,
  requesterDisplayName,
) {
  if (!tenantId) throw new Error('tenantId is required');
  if (!requestUid) throw new Error('requestUid is required');
  if (!role) throw new Error('role is required');
  if (role === 'platform_admin') throw new Error('Cannot provision as platform_admin');

  const db = getDb();
  const batch = writeBatch(db);

  const now = serverTimestamp();
  const safeSpecialtyIds = Array.isArray(specialtyIds) ? specialtyIds : [];

  // 1. Update the join request → status: 'approved'
  const joinReqRef = doc(db, PATHS.joinRequest(tenantId, requestUid));
  batch.update(joinReqRef, {
    status: 'approved',
    decidedAt: now,
    decidedByUid: adminUid,
    provisionedRole: role,
    provisionedSpecialtyIds: safeSpecialtyIds,
  });

  // 2. Update /users/{uid} → assign tenantId, role, specialtyIds, activate
  const userRef = doc(db, PATHS.user(requestUid));
  batch.update(userRef, {
    tenantId,
    role,
    specialtyIds: safeSpecialtyIds,
    isActive: true,
    updatedAt: now,
  });

  // 3. SetDoc /tenants/{tenantId}/members/{uid} — mirror for fast tenant-scoped queries
  const memberRef = doc(db, PATHS.tenantMember(tenantId, requestUid));
  batch.set(memberRef, {
    uid: requestUid,
    tenantId,
    role,
    email: requesterEmail ?? null,
    displayName: requesterDisplayName ?? null,
    specialtyIds: safeSpecialtyIds,
    isActive: true,
    createdAt: now,
    createdByUid: adminUid,
    updatedAt: now,
  });

  await batch.commit();
  return { success: true };
}

// ============================================================================
// denyJoinRequest
//
// Marks a join request as 'denied'. The user's /users doc is NOT changed.
// Firestore rules: allow update if isTenantAdmin(tenantId)
// ============================================================================

/**
 * @param {string} tenantId
 * @param {string} requestUid
 * @param {string} adminUid
 * @returns {Promise<void>}
 */
export async function denyJoinRequest(tenantId, requestUid, adminUid) {
  const db = getDb();
  const ref = doc(db, PATHS.joinRequest(tenantId, requestUid));
  await updateDoc(ref, {
    status: 'denied',
    decidedAt: serverTimestamp(),
    decidedByUid: adminUid,
  });
}
